import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getCompanyHistory,
  saveCompanySettings,
  uploadCompanyLogo,
  uploadTeamProfilePhoto,
} from '../../services/quoteProposalService';
import type { SettingsTeamMember } from '../../services/quoteProposalService';
import TemplateSettingsSection from './TemplateSettingsSection';
import ProposalConfigurationSection from './ProposalConfigurationSection';
import { useToast } from '../../context/ToastContext';

type SettingsTab = 'company' | 'appearance' | 'config';

interface QuoteProposalSettingsProps {
  onBack: () => void;
}

const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];
const QUOTE_TYPE_OPTIONS = ['auto', 'manual'];

/* ── Signature canvas hook ─────────────────────────────────── */
function useSignatureCanvas(canvasRef: React.RefObject<HTMLCanvasElement | null>, onEnd: (dataUrl: string) => void) {
  const drawing = useRef(false);
  const lastPos = useRef<{ x: number; y: number } | null>(null);

  const getPos = (e: MouseEvent | TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: (e as MouseEvent).clientX - rect.left, y: (e as MouseEvent).clientY - rect.top };
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    ctx.strokeStyle = '#4B0082';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';

    const start = (e: MouseEvent | TouchEvent) => {
      drawing.current = true;
      lastPos.current = getPos(e, canvas);
    };
    const move = (e: MouseEvent | TouchEvent) => {
      if (!drawing.current || !lastPos.current) return;
      e.preventDefault();
      const pos = getPos(e, canvas);
      ctx.beginPath();
      ctx.moveTo(lastPos.current.x, lastPos.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      lastPos.current = pos;
    };
    const end = () => {
      if (!drawing.current) return;
      drawing.current = false;
      lastPos.current = null;
      onEnd(canvas.toDataURL());
    };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [canvasRef, onEnd]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height);
  }, [canvasRef]);

  return { clear };
}

/* ── Signature sub-component ─────────────────────────────────── */
interface SignatureProps {
  member: SettingsTeamMember;
  index: number;
  onChange: (index: number, field: keyof SettingsTeamMember, value: any) => void;
}

const MemberSignature: React.FC<SignatureProps> = ({ member, index, onChange }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const option = member.selectedOption || 'draw';

  const { clear } = useSignatureCanvas(canvasRef, (dataUrl) => {
    onChange(index, 'drawnSignature', dataUrl);
    onChange(index, 'selectedOption', 'draw');
  });

  return (
    <div className="qp-sig">
      <div className="qp-sig__tabs">
        {(['draw', 'type', 'upload'] as const).map((opt) => (
          <button
            key={opt}
            className={`qp-sig__tab${option === opt ? ' qp-sig__tab--active' : ''}`}
            onClick={() => onChange(index, 'selectedOption', opt)}
          >
            {opt.charAt(0).toUpperCase() + opt.slice(1)}
          </button>
        ))}
      </div>

      {option === 'draw' && (
        <div className="qp-sig__panel">
          <p className="qp-sig__hint">Draw your signature below</p>
          <canvas
            ref={canvasRef}
            width={320}
            height={120}
            className="qp-sig__canvas"
          />
          {member.drawnSignature && (
            <div className="qp-sig__preview">
              <span className="qp-sig__preview-label">Preview</span>
              <img src={member.drawnSignature} alt="Drawn signature" className="qp-sig__preview-img" />
            </div>
          )}
          <button className="qp-sig__clear-btn" onClick={clear}>Clear</button>
        </div>
      )}

      {option === 'type' && (
        <div className="qp-sig__panel">
          <input
            className="qp-settings__input"
            type="text"
            placeholder="Type your signature…"
            value={member.typedSignature || ''}
            onChange={(e) => {
              onChange(index, 'typedSignature', e.target.value);
              onChange(index, 'selectedOption', 'type');
            }}
          />
          {member.typedSignature && (
            <div className="qp-sig__typed-preview">{member.typedSignature}</div>
          )}
        </div>
      )}

      {option === 'upload' && (
        <div className="qp-sig__panel">
          <p className="qp-sig__hint">Upload an image of your signature</p>
          <input
            type="file"
            accept=".jpeg,.jpg,.png,image/jpeg,image/png"
            className="qp-settings__input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onloadend = () => {
                onChange(index, 'uploadedSignature', reader.result as string);
                onChange(index, 'selectedOption', 'upload');
              };
              reader.readAsDataURL(file);
            }}
          />
          {member.uploadedSignature && (
            <div className="qp-sig__preview">
              <img src={member.uploadedSignature} alt="Uploaded signature" className="qp-sig__preview-img" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Main component ─────────────────────────────────────────── */
const QuoteProposalSettings: React.FC<QuoteProposalSettingsProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('company');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  // Company fields
  const [companyId, setCompanyId] = useState<string | number>('');
  const [companyName, setCompanyName] = useState('');
  const [website, setWebsite] = useState('');
  const [overview, setOverview] = useState('');
  const [aboutUs, setAboutUs] = useState('');
  const [vision, setVision] = useState('');
  const [mission, setMission] = useState('');
  const [address, setAddress] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#8b5cf6');
  const [secondaryColor, setSecondaryColor] = useState('#6d28d9');
  const [defaultQuoteType, setDefaultQuoteType] = useState('auto');
  const [autoSaveDrafts, setAutoSaveDrafts] = useState(true);
  const [displayCurrency, setDisplayCurrency] = useState('USD');
  const [emailNotification, setEmailNotification] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(1);
  const [selectedCustomTemplate, setSelectedCustomTemplate] = useState(0);
  const [selectedCustomTemplateName, setSelectedCustomTemplateName] = useState('');

  // Team members
  const [teamMembers, setTeamMembers] = useState<SettingsTeamMember[]>([
    { name: '', role: '', profilePhoto: '', signature: '', selectedOption: 'draw', typedSignature: '', drawnSignature: '', uploadedSignature: null },
  ]);

  // Field errors
  const [nameError, setNameError] = useState('');
  const [websiteError, setWebsiteError] = useState('');
  const [aboutUsError, setAboutUsError] = useState('');
  const [addressError, setAddressError] = useState('');

  // Expanded signature per member
  const [expandedSigIndex, setExpandedSigIndex] = useState<number | null>(null);

  // Logo upload
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Load settings
  useEffect(() => {
    setLoading(true);
    getCompanyHistory().then((data) => {
      if (data) {
        setCompanyId(data.companyId || '');
        setCompanyName(data.name || '');
        setWebsite(data.website || '');
        setOverview(data.overview || '');
        setAboutUs(data.aboutUs || '');
        setVision(data.vision || '');
        setMission(data.mission || '');
        setAddress(data.address || '');
        setLogoUrl(data.logoUrl || '');
        setPrimaryColor(data.primaryColour?.trim() ? data.primaryColour : '#8b5cf6');
        setSecondaryColor(data.secondaryColour?.trim() ? data.secondaryColour : '#6d28d9');
        setDefaultQuoteType(data.defaultQuoteType || 'auto');
        setAutoSaveDrafts(data.autoSaveDrafts ?? true);
        setDisplayCurrency(data.displayCurrency || 'USD');
        setEmailNotification(data.emailNotification ?? true);
        setSelectedTemplate(Number(data.templateId) || 1);
        setSelectedCustomTemplate(data.customTemplateId || 0);
        setSelectedCustomTemplateName(data.customTemplateName || '');
        if (data.teamMembers) {
          try {
            const parsed = typeof data.teamMembers === 'string' ? JSON.parse(data.teamMembers) : data.teamMembers;
            if (Array.isArray(parsed) && parsed.length > 0) setTeamMembers(parsed);
          } catch { /* keep default */ }
        }
      }
    }).finally(() => setLoading(false));
  }, []);

  const validate = () => {
    let ok = true;
    setNameError(''); setWebsiteError(''); setAboutUsError(''); setAddressError('');
    if (!companyName.trim()) { setNameError('Please enter company name'); ok = false; }
    if (!website.trim()) { setWebsiteError('Please enter website'); ok = false; }
    if (!aboutUs.trim()) { setAboutUsError('Please enter about us'); ok = false; }
    if (!address.trim()) { setAddressError('Please enter address'); ok = false; }
    return ok;
  };

  const handleSave = async () => {
    if (!validate()) { setActiveTab('company'); return; }
    setSaving(true);

    const validMembers = teamMembers.filter((m) =>
      m.name.trim() || m.role.trim() || m.profilePhoto.trim()
    );

    const ok = await saveCompanySettings({
      companyId: String(companyId),
      name: companyName,
      website,
      aboutUs,
      address,
      logoUrl,
      defaultQuoteType,
      autoSaveDrafts,
      displayCurrency,
      emailNotification,
      primaryColour: primaryColor,
      secondaryColour: secondaryColor,
      overview,
      vision,
      mission,
      templateId: selectedTemplate,
      customTemplateId: selectedCustomTemplate,
      customTemplateName: selectedCustomTemplateName,
      teamMembers: JSON.stringify(validMembers),
      signature: '',
    });

    setSaving(false);
    showToast(ok ? 'success' : 'error', ok ? 'Settings saved successfully' : 'Failed to save settings');
  };

  const handleLogoUpload = async (file: File) => {
    setLogoUploading(true);
    const url = await uploadCompanyLogo(file);
    setLogoUploading(false);
    if (url) { setLogoUrl(url); showToast('success', 'Logo uploaded'); }
    else showToast('error', 'Failed to upload logo');
  };

  const handleMemberChange = <K extends keyof SettingsTeamMember>(index: number, field: K, value: SettingsTeamMember[K]) => {
    setTeamMembers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleMemberPhotoUpload = async (file: File, index: number) => {
    const url = await uploadTeamProfilePhoto(file);
    if (url) handleMemberChange(index, 'profilePhoto', url);
    else showToast('error', 'Failed to upload photo');
  };

  const handleAddMember = () => {
    setTeamMembers((prev) => [...prev, {
      name: '', role: '', profilePhoto: '', signature: '',
      selectedOption: 'draw', typedSignature: '', drawnSignature: '', uploadedSignature: null,
    }]);
  };

  const handleDeleteMember = (index: number) => {
    setTeamMembers((prev) => prev.filter((_, i) => i !== index));
    if (expandedSigIndex === index) setExpandedSigIndex(null);
  };

  const onTemplateSelect = (id: number) => {
    setSelectedTemplate(id);
    setSelectedCustomTemplate(0);
    setSelectedCustomTemplateName('');
  };

  const onCustomTemplateSelect = (id: number, name: string, parentId: number) => {
    setSelectedCustomTemplate(id);
    setSelectedCustomTemplateName(name);
    if (parentId) setSelectedTemplate(parentId);
  };

  const tabs: { id: SettingsTab; label: string }[] = [
    { id: 'company', label: 'Company' },
    { id: 'appearance', label: 'Appearance' },
    { id: 'config', label: 'Proposal Configuration' },
  ];

  const SaveButton = () => (
    <div className="qp-settings__footer">
      <button className="qp-settings__save-btn" onClick={handleSave} disabled={saving}>
        {saving ? (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
            Saving…
          </>
        ) : (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            Save Changes
          </>
        )}
      </button>
    </div>
  );

  return (
    <div className="qp-settings">
      {/* Header */}
      <div className="qp-settings__header">
        <div className="qp-settings__title-row">
          <button className="qp-settings__back-btn" onClick={onBack} title="Back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
          </button>
          <div>
            <h2 className="qp-settings__title">Settings</h2>
            <p className="qp-settings__subtitle">Manage your account and preferences</p>
          </div>
        </div>
        <div className="qp-settings__tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              className={`qp-settings__tab${activeTab === t.id ? ' qp-settings__tab--active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="qp-settings__body">
        {loading ? (
          <div className="qp-settings__loader">
            <div className="qp-history-loader__orb">
              <div className="qp-history-loader__ring qp-history-loader__ring--1"/>
              <div className="qp-history-loader__ring qp-history-loader__ring--2"/>
              <div className="qp-history-loader__core">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>
              </div>
            </div>
          </div>
        ) : (
          <div className="qp-settings__content">

            {/* ── Company Tab ── */}
            {activeTab === 'company' && (
              <div className="qp-settings__section">
                {/* About Your Company */}
                <div className="qp-settings__section-header">
                  <h3 className="qp-settings__section-title">About Your Company</h3>
                  <p className="qp-settings__section-desc">Add information about your business that will appear on quotes.</p>
                </div>
                <div className="qp-settings__grid qp-settings__grid--2">
                  <div className="qp-settings__field">
                    <label className="qp-settings__label">Company Name <span className="qp-settings__required">*</span></label>
                    <input
                      className={`qp-settings__input${nameError ? ' qp-settings__input--error' : ''}`}
                      type="text"
                      value={companyName}
                      onChange={(e) => { setCompanyName(e.target.value); setNameError(''); }}
                      placeholder="e.g. ABC Insurance Group"
                    />
                    {nameError && <span className="qp-settings__field-error">{nameError}</span>}
                  </div>
                  <div className="qp-settings__field">
                    <label className="qp-settings__label">Website <span className="qp-settings__required">*</span></label>
                    <div className="qp-settings__input-icon-wrap">
                      <svg className="qp-settings__input-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                      <input
                        className={`qp-settings__input qp-settings__input--with-icon${websiteError ? ' qp-settings__input--error' : ''}`}
                        type="text"
                        value={website}
                        onChange={(e) => { setWebsite(e.target.value); setWebsiteError(''); }}
                        placeholder="https://www.yoursite.com"
                      />
                    </div>
                    {websiteError && <span className="qp-settings__field-error">{websiteError}</span>}
                  </div>
                </div>
                <div className="qp-settings__grid qp-settings__grid--2">
                  <div className="qp-settings__field">
                    <label className="qp-settings__label">Company Overview</label>
                    <textarea className="qp-settings__textarea" rows={4} value={overview} onChange={(e) => setOverview(e.target.value)} placeholder="Describe your company overview…"/>
                  </div>
                  <div className="qp-settings__field">
                    <label className="qp-settings__label">About Us <span className="qp-settings__required">*</span></label>
                    <textarea className={`qp-settings__textarea${aboutUsError ? ' qp-settings__input--error' : ''}`} rows={4} value={aboutUs} onChange={(e) => { setAboutUs(e.target.value); setAboutUsError(''); }} placeholder="Describe your company and services…"/>
                    {aboutUsError && <span className="qp-settings__field-error">{aboutUsError}</span>}
                  </div>
                </div>
                <div className="qp-settings__grid qp-settings__grid--2">
                  <div className="qp-settings__field">
                    <label className="qp-settings__label">Vision</label>
                    <textarea className="qp-settings__textarea" rows={4} value={vision} onChange={(e) => setVision(e.target.value)} placeholder="Your company vision…"/>
                  </div>
                  <div className="qp-settings__field">
                    <label className="qp-settings__label">Mission</label>
                    <textarea className="qp-settings__textarea" rows={4} value={mission} onChange={(e) => setMission(e.target.value)} placeholder="Your company mission…"/>
                  </div>
                </div>
                <div className="qp-settings__field">
                  <label className="qp-settings__label">Address <span className="qp-settings__required">*</span></label>
                  <input
                    className={`qp-settings__input${addressError ? ' qp-settings__input--error' : ''}`}
                    type="text"
                    value={address}
                    onChange={(e) => { setAddress(e.target.value); setAddressError(''); }}
                    placeholder="123 Main St, City, State, ZIP"
                  />
                  {addressError && <span className="qp-settings__field-error">{addressError}</span>}
                </div>

                {/* Team Members */}
                <div className="qp-settings__section-header" style={{ marginTop: 8 }}>
                  <h3 className="qp-settings__section-title">Team Members</h3>
                  <p className="qp-settings__section-desc">Add team members that will appear on your proposals.</p>
                </div>
                <div className="qp-team-grid">
                  {teamMembers.map((member, index) => (
                    <div key={index} className="qp-team-card">
                      <div className="qp-team-card__toolbar">
                        <button className="qp-team-card__delete" onClick={() => handleDeleteMember(index)} title="Remove">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                        </button>
                      </div>

                      {/* Photo */}
                      <label className="qp-team-card__photo-wrap" htmlFor={`member-photo-${index}`} title="Upload photo">
                        {member.profilePhoto ? (
                          <img src={member.profilePhoto} alt="Member" className="qp-team-card__photo" />
                        ) : (
                          <div className="qp-team-card__photo-placeholder">
                            {member.name ? member.name.split(' ').map((w) => w[0]).slice(0, 2).join('') : (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                            )}
                          </div>
                        )}
                        <div className="qp-team-card__camera">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                        </div>
                        <input id={`member-photo-${index}`} type="file" accept=".jpeg,.jpg,.png,image/jpeg,image/png" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleMemberPhotoUpload(e.target.files[0], index); }} />
                      </label>

                      <div className="qp-team-card__fields">
                        <input
                          className="qp-settings__input"
                          type="text"
                          placeholder="Name"
                          value={member.name}
                          onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                        />
                        <input
                          className="qp-settings__input"
                          type="text"
                          placeholder="Role / Title"
                          value={member.role}
                          onChange={(e) => handleMemberChange(index, 'role', e.target.value)}
                        />

                        {/* Signature accordion */}
                        <button
                          className="qp-team-card__sig-toggle"
                          onClick={() => setExpandedSigIndex(expandedSigIndex === index ? null : index)}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/></svg>
                          {expandedSigIndex === index ? 'Hide Signature' : `Signature for ${member.name || `Member ${index + 1}`}`}
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: expandedSigIndex === index ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                        </button>

                        {expandedSigIndex === index && (
                          <MemberSignature member={member} index={index} onChange={handleMemberChange} />
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Add member card */}
                  <button className="qp-team-card qp-team-card--add" onClick={handleAddMember}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                    <span>Add Team Member</span>
                  </button>
                </div>

                <SaveButton />
              </div>
            )}

            {/* ── Appearance Tab ── */}
            {activeTab === 'appearance' && (
              <div className="qp-settings__section">
                {/* Logo */}
                <div className="qp-settings__section-header">
                  <h3 className="qp-settings__section-title">Logo</h3>
                  <p className="qp-settings__section-desc">Upload your company logo.</p>
                </div>
                <div className="qp-settings__logo-zone">
                  {logoUploading ? (
                    <div className="qp-settings__logo-loading">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                    </div>
                  ) : logoUrl ? (
                    <div className="qp-settings__logo-preview">
                      <img src={logoUrl} alt="Company logo" className="qp-settings__logo-img" />
                      <div className="qp-settings__logo-actions">
                        <label className="qp-settings__logo-replace-btn" htmlFor="logo-upload">
                          Replace
                          <input id="logo-upload" type="file" accept=".jpeg,.jpg,.png" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleLogoUpload(e.target.files[0]); }} />
                        </label>
                        <button className="qp-tpl-cancel-btn" onClick={() => setLogoUrl('')}>Remove</button>
                      </div>
                    </div>
                  ) : (
                    <label className="qp-settings__logo-upload" htmlFor="logo-upload">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      <span>Upload Logo</span>
                      <span className="qp-settings__logo-hint">JPEG, JPG or PNG</span>
                      <input id="logo-upload" type="file" accept=".jpeg,.jpg,.png" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleLogoUpload(e.target.files[0]); }} />
                    </label>
                  )}
                </div>

                {/* Brand Colors */}
                <div className="qp-settings__section-header" style={{ marginTop: 8 }}>
                  <h3 className="qp-settings__section-title">Brand Colors</h3>
                  <p className="qp-settings__section-desc">Choose your brand's colors</p>
                </div>
                <div className="qp-settings__grid qp-settings__grid--2">
                  <div className="qp-settings__field">
                    <label className="qp-settings__label">Primary Color</label>
                    <div className="qp-settings__color-row">
                      <div className="qp-settings__color-swatch" style={{ background: primaryColor }} />
                      <input type="color" className="qp-settings__color-input" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} />
                      <input className="qp-settings__input" type="text" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} maxLength={7} placeholder="#8b5cf6" />
                    </div>
                  </div>
                  <div className="qp-settings__field">
                    <label className="qp-settings__label">Secondary Color</label>
                    <div className="qp-settings__color-row">
                      <div className="qp-settings__color-swatch" style={{ background: secondaryColor }} />
                      <input type="color" className="qp-settings__color-input" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} />
                      <input className="qp-settings__input" type="text" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} maxLength={7} placeholder="#6d28d9" />
                    </div>
                  </div>
                </div>
                {/* Color preview bar */}
                <div className="qp-settings__color-preview-bar">
                  <div className="qp-settings__color-preview-swatch" style={{ background: primaryColor }}>Primary</div>
                  <div className="qp-settings__color-preview-swatch" style={{ background: secondaryColor }}>Secondary</div>
                </div>

                {/* Templates */}
                <TemplateSettingsSection
                  selectedTemplate={selectedTemplate}
                  selectedCustomTemplate={selectedCustomTemplate}
                  onTemplateSelect={onTemplateSelect}
                  onCustomTemplateSelect={onCustomTemplateSelect}
                  companyLogo={logoUrl}
                  showToast={showToast}
                />

                <SaveButton />
              </div>
            )}

            {/* ── Proposal Configuration Tab ── */}
            {activeTab === 'config' && (
              <div className="qp-settings__section">
                <ProposalConfigurationSection
                  templateId={selectedTemplate}
                  companyLogo={logoUrl}
                  customTemplateId={selectedCustomTemplate || undefined}
                  onTemplateChange={(tId, cId) => {
                    setSelectedTemplate(tId);
                    setSelectedCustomTemplate(cId || 0);
                  }}
                  showToast={showToast}
                />

                <SaveButton />
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteProposalSettings;
